<template lang="pug">
span
  span(@click="handleClick")
    slot(name="activator")
  v-form(
    ref="form"
    v-model="valid"
    lazy-validation
  )
    slide-drawer(
      v-model="showSlideDrawer"
      :confirm-dismiss="confirmDismiss"
      width="480"
      height="540"
      attachToBottom
      ref="add-event-slide-drawer"
      data-cy="add-event-slide-drawer"
      draggable
    )
      template(slot="title") {{ title }}
      template(slot="content")
        v-card-text.slider-content
          v-select(
            :items="types"
            v-model="selectedType"
            :disabled="!!edit"
            label="Type"
            :menu-props="{ maxHeight: 300 }"
            data-cy="event-modal-types"
          )
          v-autocomplete(
            v-if="showContactSelection && !edit"
            label="Select Contact(s)"
            v-model="selectedContacts"
            :items="contacts"
            :search-input.sync="contactSearch"
            return-object
            item-value="id"
            hide-no-data
            multiple
            chips
            deletable-chips
            required
            :rules="contactRules"
            data-cy="event-modal-search"
          )
          rich-text-editor(
            v-if="isCommentType"
            data-cy="comment-editor-rich-text-editor"
            toolType="event"
            communicationType="event"
            v-model="description"
            :attachmentHandlerId="attachmentHandlerId"
          )
          v-textarea(
            v-else
            v-model="description"
            label="Description"
            data-cy="event-modal-description"
            :error-messages="notSharedContactError"
          )
          span.limit(
            v-if="isCommentType"
            :class="{'overLimit': isEventLengthOverLimit }"
          ) {{eventLengthLimit}}
          div(v-if="isCommentType") NOTE: Links to uploaded files are public. Do not upload files of a sensitive or confidential nature.
          attachment-handler(
            :input-id="attachmentHandlerId"
            :files="eventFiles"
            :uploadedFiles="editEventFiles"
            @change="handleUpload"
            @complete="handleUpload"
            @error="handleUploadError"
          )
          liondesk-dialog(
            v-if="showFileError"
            :value="showFileError"
            dialogTitle="Failed to attach file"
            cancelButtonText="Close"
            okButtonText="Try Another File"
            @click:cancel="closeErrors"
            @click:ok="tryUploadAgain"
          )
            div.error-text(slot="body")
              span The following file{{ fileErrors.length > 1 ? 's' : '' }} could not be attached:
              ul.files
                li(v-for="(file, index) in fileErrors" :key="index") {{ file.name }}
              span Please ensure the file size does not exceed 15 MB.
          v-card(v-if="showContactsToMention").scroll-card
            v-list(v-if="filteredTeamMembers.length").px-3
              v-checkbox.ma-0(
                v-for="contact in filteredTeamMembers" :key="contact.id"
                v-model="mentionedContacts"
                :label="contact.text"
                :value="contact.id"
                :disabled="contact.no_contact_access"
                @change="showUser(contact, $event)")
            span(v-if="!filteredTeamMembers.length").center-text.py-1 No data available
      template(slot="actionsCustom")
        v-spacer
        .slider-actions.justify-end
          v-btn(flat @click="cancel") Cancel
          v-btn.primary(@click="save" :disabled="!allFilesUploaded" data-cy="add-event") {{ buttonCta }}
</template>

<script>
import { mapGetters, mapActions, mapState, createNamespacedHelpers } from 'vuex';
import SlideDrawer from '@/Core/components/SlideDrawer';
import RichTextEditor from '@/Core/components/richTextareaEditor/RichTextEditor';
import AttachmentHandler from '@/Communications/components/AttachmentHandler';
import toggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import FileUpload from '@/Core/mixins/file-upload';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import { eventTypes } from '@/Contacts/config/event-types';
import { debounce, prepareRequest } from '@/Core/utilities';
import { MAX_DESCRIPTION_LENGTH } from '@/Core/config/events';
const CONTACT_ACCESS_ERROR = " (Does not have access to contact)";
const CONTACT_NOT_SHARED = "(not shared with team members)";
const CONTACT_NOT_SHARED_SELECTED_ERROR = `You have selected a contact that is
 not being shared with team members, this will cause to not notify any
 mentioned team members`;

export default {
  components: {
    SlideDrawer,
    RichTextEditor,
    AttachmentHandler,
    LiondeskDialog,
  },
  mixins: [toggleSlideDrawer, FileUpload],
  props: {
    validateDescription: {
      type: Function
    },
    showContactSelection: {
      type: Boolean,
      default: false,
    },
    addEvents: {
      type: Function,
    },
    updateEvent: {
      type: Function,
    },
    selectedIds: {
      type: Array,
    },
    isDashboard: {
      type: Boolean,
      default: false,
    },
    processBulkAction: {
      type: Function,
    },
    isBulk: {
      type: Boolean,
      default: false,
    },
    isComment: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Object,
      default: null,
    }
  },
  data() {
    return {
      selectedType: eventTypes[1].value,
      description: '',
      contactSearch: null,
      selectedContacts: [],
      valid: true,
      contactRules: [contacts => contacts.length !== 0 || 'At least one contact is required'],
      mentionedContacts: [],
      isMentioningUser: false,
      filterMentionedTeamMember: '',
      CONTACT_COMMENTS: 'Contact Comments',
      previousMentionedContacts: [],
      contactTeamsIds: new Set(),
      notSharedContactError: "",
      attachmentHandlerId: "event-drawer-attachment-handler",
      eventFiles: [],
      editEventFiles: [],
    };
  },
  watch: {
    selectedContacts(value) {
      let anyHiddenContactSelected = value.some(contact => contact.is_hidden_from_team);
      this.notSharedContactError = anyHiddenContactSelected ? CONTACT_NOT_SHARED_SELECTED_ERROR : "";
    },
    contactSearch(value) {
      this.setSearch(value);
      if (!value) {
        this.resetSearchResults();
      } else {
        debounce(prepareRequest, [value, this.searchContacts]);
      }
    },
    description(value) {
      if (!this.isCommentType) return;
      if (this.mentionedContacts.length) {
        this.mentionedContacts = this.mentionedContacts.filter(contactId => {
          const member = this.teamMembers.find(member => member.id == contactId);
          return value.includes(member.text.toUpperCase());
        });
      }
      const MENTION_PATTERN = /\B@[a-z0-9_-]+/gi;
      const mentions = value.match(MENTION_PATTERN);
      if (mentions) {
        this.isMentioningUser = true;
        this.filterMentionedTeamMember = mentions[mentions.length - 1].substring(1);
      } else {
        this.filterMentionedTeamMember = '';
        this.isMentioningUser = false;
      }
    },
    selectedType(value) {
      this.$emit('setIsComment', value === this.CONTACT_COMMENTS);
    },
    edit(value) {
      if (value) {
        var res = this.stripAttachmentsHTML(value.description);
        this.eventFiles = res.data;
        this.editEventFiles = res.data;
        this.description = res.description;
      } else {
        this.description = ''
        this.eventFiles = [];
        this.editEventFiles = [];
      }
    },
    showSlideDrawer(value) {
      if (!value) return;
      
      this.selectedType = this.isComment ? eventTypes[0].value : eventTypes[1].value;
    },
  },

  computed: {
    ...mapState({
      teamMembersList: state => state.teamMembers.teamMembers,
      userInformation: state => state.userInformation.user,
      contact: state => state.contact.contact,
      userTeams: state => state.userInformation.userTeams,
      searchResults: state => state.contactsSearch.searchResults,
    }),
    ...mapGetters({
      contactsList: 'contactsSearch/contactsList',
      userHasFeatureFlag: 'userInformation/userHasFeatureFlag'
    }),
    isContactProfile() {
      return Object.keys(this.contact).length && !this.isDashboard;
    },
    teamMembers() {
      if (!this.userInformation || !this.teamMembersList) {
        return [];
      }
      const teamInfo = this.teamMembersList.filter( member => member.user.id != this.userInformation.id);
      if(this.isContactProfile) {
        this.getCurrentContactTeamsIds(teamInfo);
      }
      let teamMembersInfo = teamInfo.map(member => {
        let name = `${member.user.first_name} ${member.user.last_name}`;
        let teamMember = {
          text: name,
          id: member.user.id,
          email: member.user.email,
          no_contact_access: false,
        }
        if(this.isAnySelectedContactNotShared() || this.isMentionedMembertPartOfAnySelectedContactTeams(member)) {
          teamMember.text = name + CONTACT_ACCESS_ERROR;
          teamMember.no_contact_access = true;
        }
        return teamMember;
      }, this);
      if(this.isContactProfile && !this.isAnySelectedContactNotShared()) {
        teamMembersInfo = this.filterRepeatedTeamMembers(teamMembersInfo);
      }
      return teamMembersInfo.filter((teamMember, index) => {
        return index === teamMembersInfo.findIndex(obj => {
          return JSON.stringify(obj) === JSON.stringify(teamMember);
        });
      });
    },
    contacts() {
      /**
       * this.selectedContacts must remain in this list at all times or
       * else previously selected contacts will occasionally disappear on
       * the input field
       */
      let contactsList = this.contactsList.map(contact => {
        if(contact.is_hidden_from_team && !contact.text.match(CONTACT_NOT_SHARED)){
          contact.text = contact.text + " " + CONTACT_NOT_SHARED;
        }
        return contact;
      });
      return this.selectedContacts.concat(contactsList);
    },
    title() {
      let type = 'Activity'
      if (this.isCommentType) {
        type = 'Comment'
      }
      let action = 'Add'
      if (this.edit) {
        action = 'Edit'
      }
      if (this.selectedIds) {
        if (this.selectedIds.length <= 1) {
          return `${action} ${type} for Contact`;
        }
        return `${action} ${type} for ${this.selectedIds.length} Contacts`;
      }
      return `${action} ${type}`;
    },
    buttonCta() {
      let type = 'Activity'
      if (this.isCommentType) {
        type = 'Comment'
      }
      let action = 'Add'
      if (this.edit) {
        action = 'Edit'
      }
      return `${action} ${type}`;
    },
    types() {
      return eventTypes;
    },
    filteredTeamMembers() {
      return this.teamMembers.filter(user => user.text.toUpperCase().includes(this.filterMentionedTeamMember.toUpperCase()));
    },
    isCommentType() {
      return this.selectedType == this.CONTACT_COMMENTS;
    },
    showContactsToMention() {
      return this.teamMembers.length && this.isMentioningUser && this.isCommentType && !this.edit;
    },
    eventLengthLimit() {
      return `${this.description.length + this.attachmentsHTML.length}/${MAX_DESCRIPTION_LENGTH}`;
    },
    isEventLengthOverLimit() {
      return this.description.length + this.attachmentsHTML.length > MAX_DESCRIPTION_LENGTH;
    },
    attachmentsHTML() {
      if (!this.eventFiles || !this.eventFiles.length) return '';
      var divString = '<div class="event-attachments">';
        this.eventFiles.forEach(function(item) {
          var titleAttributeValue = `${item.type}!${item.size_bytes}`;
          var aString = `&nbsp;<a href="${item.path}" title="${titleAttributeValue}" id="${item.id}" target="_blank">${item.name}</a>`;
          divString += aString;
        });
      divString += '</div>';
      return divString;
    },
    allFilesUploaded() {
      return this.eventFiles.every(item => item.progress === 100);
    },
  },

  methods: {
    ...mapActions('contactsSearch', [
      'setSearch',
      'setSearchResults',
      'searchContacts',
      'resetSearchResults',
    ]),
    ...mapActions('contactsTable', ['findSelectedContacts']),
    ...mapActions('contact', ['notifyMentionedUser']),
    ...mapActions('userInformation', ['getUserTeams']),
    ...mapActions(  { getInitialContacts: 'contactsSearch/findContacts'}),
    handleClick() {
      this.getUserTeamsIfNotLoaded();
      this.getInitialContactsIfNotLoaded();
      this.openSlideDrawer();
    },
    getUserTeamsIfNotLoaded() {
      if (!this.userTeams || this.userTeams.length === 0) {
        this.getUserTeams();
      }
    },
    getInitialContactsIfNotLoaded() {
      if (this.searchResults.length === 0) {
        this.getInitialContacts();
      }
    },
    async save() {
      if (this.$refs.form.validate() && this.validateDescription(`${this.description}${this.attachmentsHTML}`)) {
        if (this.edit) {
          const editPayload = {
          eventId: this.edit.id,
          data: {
            contact_id: this.edit.contactId,
            description: `${this.description}${this.attachmentsHTML}`,
            },
          };
          this.closeSlideDrawer(false, 'add-event-slide-drawer');
          await this.updateEvent(editPayload);
        } else {
          const contacts = this.showContactSelection
          ? this.selectedContacts.map(contact => contact.id)
          : this.selectedIds;
          if (this.mentionedContacts.length && this.isCommentType && this.notSharedContactError == "") {
            await this.sendEmailToMentionedTeamMembers(contacts);
          }
          const event = {
            selectedContacts: contacts,
            type: this.selectedType,
            description: `${this.description}${this.attachmentsHTML}`,
            query: this.query,
          };
          this.closeSlideDrawer(false, 'add-event-slide-drawer');
          if (this.isBulk) {
            const body = {
              selected: contacts,
              action: {
                type: 'CREATE',
                resource: 'events',
                payload: {
                  type: this.selectedType,
                  description: `${this.description}${this.attachmentsHTML}`,
                },
              }
            }
            await this.processBulkAction(body);
          } else {
            await this.addEvents(event);
          }
        }

        this.resetForm();
        this.resetValidation();
        this.$emit('contact-updated');
        this.$emit('deselect-all');
        this.mentionedContacts = [];
        this.isMentioningUser = false;
        this.cancel();
      }
    },
    cancel() {
      this.description = '';
      this.eventFiles = [];
      this.selectedContacts = [];
      this.mentionedContacts = [];
      this.previousMentionedContacts = [];
      this.resetSearchResults();
      this.closeSlideDrawer(false, 'add-event-slide-drawer');
      this.resetValidation();
    },
    prepareRequest(search, callback) {
      if (typeof callback !== 'function') {
        console.error('Callback is not a function');
      } else {
        callback(search);
      }
    },
    async resetForm() {
      await this.$refs.form.reset();
      this.selectedType = eventTypes[0].value;
    },
    async resetValidation() {
      await this.$refs.form.resetValidation();
      this.selectedType = eventTypes[0].value;
    },
    showUser(contact) {
      if (this.previousMentionedContacts.length < this.mentionedContacts.length) {
        const tagedUser = `@${this.filterMentionedTeamMember}`
        this.description = this.description.replace(tagedUser, contact.text.toUpperCase());
        this.filterMentionedTeamMember = '';
        this.isMentioningUser = false;
      } else {
        const contactIsMentioned = this.mentionedContacts.includes(contact.id);
        if (!contactIsMentioned) {
          this.description = this.description.replace(contact.text.toUpperCase(), '');
        }
      }
      this.setPreviousMentionedContacts();
    },
    async sendEmailToMentionedTeamMembers(contactsIds) {
      const selectedContacts = await this.findContacts(contactsIds);
      const emailInformation = this.prepareEmailInformation(selectedContacts);
      const teamMemberToMention = this.teamMembers.filter(member => this.mentionedContacts.includes(member.id));
      const mentionedUsers = teamMemberToMention.map(member => {
        return {
          id: member.id,
          email_information: emailInformation,
        }
      });
      await this.notifyMentionedUser(mentionedUsers);
    },
    prepareEmailInformation(selectedContacts) {
      const userName = `${this.userInformation.first_name} ${this.userInformation.last_name}`
      let CONTACT_INFORMATION = '';
      let CONTACTS_NAME = '';
      selectedContacts.forEach((contact, index) => {
        const isLastContact = (index + 1)  == selectedContacts.length && selectedContacts.length > 1;
        const isPenultimateContact = (index + 1)  == (selectedContacts.length - 1);
        const contactLink = `${process.env.VUE_APP_CRM_URL}/contacts/${contact.id}`;
        if (!isLastContact) {
          if (isPenultimateContact || selectedContacts.length == 1) {
            CONTACTS_NAME += `${contact.text}`;
          } else {
            CONTACTS_NAME += `${contact.text}, `;
          }
        } else {
            CONTACTS_NAME += ` and ${contact.text}`;
        }
        CONTACT_INFORMATION += 
          `<div>Contact Name: ${contact.text || ""}</div>
            <div>Contact Email: ${contact.email || ""}<br></div>
            <div>Contact Phone: ${contact.mobile_phone || ""}<br></div>
            <div><a href='${contactLink}' target="_blank">View in liondesk</a><br><br></div>`
      });
      const EMAIL_SUBJECT = `LionDesk Alert: ${userName} has mentioned you in a comment about ${CONTACTS_NAME}.`
      const EMAIL_BODY = 
        `<div><b>You were mentioned in the following ${CONTACTS_NAME} comment:</b><br><br><i>&nbsp;'${this.description}'.</i><br><br></div>`;

      return {
        email_body: EMAIL_BODY.concat(CONTACT_INFORMATION),
        email_subject: EMAIL_SUBJECT,
      }
    },
    async findContacts(contactsIds) {
      const queryParams = { id: { $in: contactsIds } };
      if(this.isContactProfile && !this.isUserOwnerOrAssigneeOfContact()) {
        queryParams.$teams = true;
      }
      const contacts = await this.findSelectedContacts(queryParams);
      return contacts.map(contact => {
        return {
          text: `${contact.first_name || ""} ${contact.last_name || ""}`,
          mobile_phone: contact.mobile_phone,
          email: contact.email,
          id: contact.id,
          is_hidden_from_team: contact.is_hidden_from_team,
        }
      })
    },
    isUserOwnerOrAssigneeOfContact() {
      return this.contact.assigned_user_id == this.userInformation.id || this.contact.owner_user_id == this.userInformation.id;
    },
    setPreviousMentionedContacts() {
      this.previousMentionedContacts = this.mentionedContacts;
    },
    isAnySelectedContactNotShared() {
      return this.isDashboard ? this.selectedContacts.some(contact => contact.is_hidden_from_team) : this.contact.is_hidden_from_team;
    },
    getCurrentContactTeamsIds(teamInfo) {
      const { assigned_user_id,  owner_user_id } = this.contact;
      const sharingEnabledTeamsId = this.userTeams.filter(team => !!team.is_contact_resource_sharing_enabled).map(team => team.id);
      if(assigned_user_id != this.userInformation.id && owner_user_id != this.userInformation.id) {
        teamInfo.forEach(member => {
          if (member.user.id == assigned_user_id || member.user.id == owner_user_id && sharingEnabledTeamsId.includes(member.team.id) ) {
            this.contactTeamsIds.add(member.team.id);
          }
        });
      } else if(assigned_user_id == this.userInformation.id || owner_user_id == this.userInformation.id) {
        sharingEnabledTeamsId.forEach(team => {
          this.contactTeamsIds.add(team);
        });
      }
    },
    findOcurrences(teamMembersInfo) {
      return teamMembersInfo.reduce((acum, current) => {
        acum[current.id] = ++acum[current.id] || 1;
        return acum;
      }, {});
    },
    /**
     * inside contact profile when creating a new activity you can have
     * the same team member(user) for different teams
     * and that user may have access to the current contact in just one of the teams
     * for example:
     * teamMembersInfo ={
     *   0: {text: "giovann cazares", id: 33708, email: "test@gmail.com", no_contact_access: false}
     *   1: {text: "giovann cazares (Does not have access to contact)", id: 33708, email: "test@gmail.com", no_contact_access: true}
     *   2: {text: "giovann cazares (Does not have access to contact)", id: 33708, email: "test@gmail.com", no_contact_access: true}
     *   3: {text: "otro", id: 33709, email: "test@gmail.com", no_contact_access: false}
     * }
     * this method will return just the object that does have access to the current contact
     */
    filterRepeatedTeamMembers(teamMembersInfo) {
      let teamMembers = [];
      let repeatedTeamMembers = [];
      let notRepeatedTeamMembers = [];
      let teamMembersCount = this.findOcurrences(teamMembersInfo);
      Object.keys(teamMembersCount).forEach(member => {
       member = parseInt(member);
       teamMembersCount[member] > 1 ? repeatedTeamMembers.push(member) : notRepeatedTeamMembers.push(member);
      });
      teamMembers = teamMembersInfo.filter(member => notRepeatedTeamMembers.includes(member.id));
      repeatedTeamMembers.forEach(memberId => {
        let repeatedMemberWithAccessIndex = teamMembersInfo.findIndex(user => user.id == memberId && !user.no_contact_access);
        if(repeatedMemberWithAccessIndex >= 0){
          teamMembers.push(teamMembersInfo[repeatedMemberWithAccessIndex]);
        } else {
        let firstNoAccessMemberIndex = teamMembersInfo.findIndex(user => user.id == memberId && user.no_contact_access);
          teamMembers.push(teamMembersInfo[firstNoAccessMemberIndex]);
        }
      });
      return teamMembers;
    },
    isMentionedMembertPartOfAnySelectedContactTeams(member) {
      if (this.isContactProfile) {
        return this.contactTeamsIds.size && !this.contactTeamsIds.has(member.team.id);
      }
      return this.selectedContacts.length && this.contactTeamsIds.size && !this.contactTeamsIds.has(member.team.id);
    },
    resetDragPosition() {
      if (this.$refs['add-event-slide-drawer']) this.$refs['add-event-slide-drawer'].resetDragPosition();
    },
    handleUpload(value) {
      this.eventFiles = [...value];
    },
    handleUploadError(value) {
      this.handleFileError(value);
      const index = this.eventFiles.findIndex(f => f.name === value.name);
      if (index) {
        const updated = [...this.eventFiles.slice(0, index), ...this.eventFiles.slice(index + 1)];
        this.eventFiles = updated;
      }
    },
    stripAttachmentsHTML(description) {
      var regex = /<div class="event-attachments">(.*?)<\/div>/;
  
      var match = regex.exec(description);
      if (match) {
        var divContent = match[1];
        var updatedHtml = description.replace(match[0], '');

        var dataArray = [];
        var aRegex = /<a\s*(?:href="([^"]*)"|href)?(?:\s*title="([^"]*)")?(?:\s*id="([^"]*)"\s*)?\s*(?:target="_blank")?(?:rel="nofollow")?>([^<]*)<\/a>/g;
        var aMatch;

        while ((aMatch = aRegex.exec(divContent)) !== null) {
          var splitTitle = aMatch[2] ? aMatch[2].split('!') : null;
          dataArray.push({
            path: aMatch[1] || '',
            type: splitTitle[0] || '',
            size_bytes: splitTitle[1] || '',
            id: aMatch[3],
            name: aMatch[4] || 'attachment',
            progress: 100,
          });
        }

        return {description: updatedHtml, data: dataArray};
      }
      return {description: description, data: []};
    },
  }
};
</script>

<style lang="scss" scoped>
.scroll-card {
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
}
.center-text {
  text-align: center;
  display: block;
}
.limit {
  opacity: 0.6;
}
.overLimit {
  color: var(--v-error-base);
}
</style>
